import React, { useState, useEffect } from 'react';
import approvalsService, { RejectedImage } from '../../services/approvals';
import './RejectedImages.css';

const RejectedImages: React.FC = () => {
  const [rejectedImages, setRejectedImages] = useState<RejectedImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });

  useEffect(() => {
    loadRejectedImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReason, dateRange]);

  const loadRejectedImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await approvalsService.getRejectedImages({
        reason: selectedReason,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      setRejectedImages(response);
    } catch (err) {
      setError('Failed to load rejected images');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Group rejected images by reason
  const groupByReason = () => {
    const grouped: { [key: string]: RejectedImage[] } = {};
    rejectedImages.forEach(img => {
      const reason = img.rejection_reason || 'Unknown';
      if (!grouped[reason]) {
        grouped[reason] = [];
      }
      grouped[reason].push(img);
    });
    return grouped;
  };

  // Get unique rejection reasons for filter
  const uniqueReasons = Array.from(new Set(rejectedImages.map(img => img.rejection_reason))).filter(Boolean);

  const groupedImages = groupByReason();

  return (
    <div className="rejected-images">
      <div className="page-header">
        <h1>Rejected Images Analysis</h1>
        <p className="page-subtitle">
          Analyze rejected images to improve pipeline quality
        </p>
      </div>

      {/* Filters */}
      <div className="filter-section">
        <div className="filter-group">
          <label>Rejection Reason</label>
          <select
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
            className="form-select"
          >
            <option value="">All Reasons</option>
            {uniqueReasons.map(reason => (
              <option key={reason} value={reason}>
                {reason} ({rejectedImages.filter(img => img.rejection_reason === reason).length})
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Date Range</label>
          <div className="date-range">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="form-input"
            />
            <span>to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="form-input"
            />
          </div>
        </div>

        <button onClick={loadRejectedImages} className="btn btn-primary">
          Apply Filters
        </button>
      </div>

      {/* Statistics */}
      <div className="rejection-stats">
        <div className="stat-card">
          <h3>Total Rejected</h3>
          <p className="stat-value">{rejectedImages.length}</p>
        </div>
        <div className="stat-card">
          <h3>Most Common Reason</h3>
          <p className="stat-value">
            {Object.entries(groupedImages).sort((a, b) => b[1].length - a[1].length)[0]?.[0] || 'N/A'}
          </p>
        </div>
        <div className="stat-card">
          <h3>Rejection Rate</h3>
          <p className="stat-value">
            {/* This would need total processed count from backend */}
            Calculating...
          </p>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading rejected images...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={loadRejectedImages} className="btn btn-primary">
            Retry
          </button>
        </div>
      ) : rejectedImages.length === 0 ? (
        <div className="empty-state">
          <h3>No rejected images found</h3>
          <p>Adjust your filters or check back later</p>
        </div>
      ) : (
        <div className="rejection-groups">
          {Object.entries(groupedImages).map(([reason, images]) => (
            <div key={reason} className="rejection-group">
              <div className="group-header">
                <h3>{reason}</h3>
                <span className="group-count">{images.length} images</span>
              </div>
              <div className="rejected-grid">
                {images.map((img) => (
                  <div key={img.id} className="rejected-card">
                    <div className="rejected-image">
                      <img
                        src={img.thumbnail_url || '/api/rejected/' + img.id + '/thumbnail'}
                        alt={img.original_filename}
                        loading="lazy"
                      />
                      <div className="quality-overlay">
                        Score: {img.quality_score || 'N/A'}
                      </div>
                    </div>
                    <div className="rejected-info">
                      <p className="filename">{img.original_filename}</p>
                      <p className="rejected-date">
                        {new Date(img.rejected_at).toLocaleDateString()}
                      </p>
                      {img.rejected_by && (
                        <p className="rejected-by">by {img.rejected_by}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RejectedImages;